/**
 * CookieConsent — our own i18n cookie banner (EV-446 + EV-453 + EV-54).
 *
 * Replaces the third-party Zoho SalesIQ GDPR banner (#gdpr_banner) which (a)
 * always rendered in ENGLISH even on Vietnamese phones, and (b) sat at bottom:0
 * with z-index 2147483647, covering the bottom nav on desktop. Zoho's banner is
 * hidden via CSS (#gdpr_banner in index.css); this is shown instead — translated
 * via react-i18next, dismissible, and offset above the bottom nav.
 *
 * EV-54 upgrade: stores { analytics: boolean, ts: number } so the first-party
 * tracker can gate on analytics consent. Both "Got it" and "Reject non-essential"
 * dismiss the banner; only "Got it" grants analytics=true.
 *
 * Backwards-compat: if the legacy truthy string (ISO date) is found, treat as
 * { analytics: true }.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import bakeLee from '@/assets/Croppedmascotts/BakeLee.png';

export const KEY = 'hl_cookie_consent';

// ─── Consent model ───────────────────────────────────────────────────────────

export interface ConsentRecord {
  analytics: boolean;
  ts: number;
}

// AN-1498 — consent is shared across every *.honestlee.app property via a
// domain-scoped cookie, so one "Got it" on the marketing site silences the
// banner in the PWA and vice versa. localStorage stays the primary store
// (per-origin); the cookie is the cross-property carrier.
const ROOT_DOMAIN = 'honestlee.app';

function readConsentCookie(): string {
  try {
    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + KEY + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  } catch { return ''; }
}

/** Returns the stored consent record, or null if the user has not decided yet. */
export function getCookieConsent(): ConsentRecord | null {
  try {
    // localStorage first, then the shared .honestlee.app cookie (set by any
    // other property, incl. the marketing sites running hl-track.js).
    const raw = localStorage.getItem(KEY) || readConsentCookie();
    if (!raw) return null;
    // Legacy: old banner stored an ISO date string directly.
    // Treat any truthy non-JSON value as analytics=true (the user had accepted).
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && 'analytics' in parsed) {
        return parsed as ConsentRecord;
      }
      // Parsed but unexpected shape — treat as accepted for backwards compat.
      return { analytics: true, ts: Date.now() };
    } catch {
      // Not valid JSON (the old ISO string) → treat as accepted.
      return { analytics: true, ts: Date.now() };
    }
  } catch {
    return null;
  }
}

/** True only if the user has explicitly accepted analytics cookies. */
export function hasAnalyticsConsent(): boolean {
  const c = getCookieConsent();
  return c !== null && c.analytics === true;
}

function saveConsent(analytics: boolean): void {
  try {
    const record: ConsentRecord = { analytics, ts: Date.now() };
    const json = JSON.stringify(record);
    localStorage.setItem(KEY, json);
    // AN-1498 — publish domain-wide so the marketing sites see it too.
    try {
      document.cookie = `${KEY}=${encodeURIComponent(json)}; path=/; max-age=31536000; `
        + `SameSite=Lax; domain=.${ROOT_DOMAIN}`;
    } catch { /* cookie blocked — localStorage still holds it for this origin */ }
    // Notify the analytics tracker (and anyone else who listens) that consent changed.
    window.dispatchEvent(new Event('hl-consent-changed'));
  } catch { /* localStorage unavailable — silently ignore */ }
}

// ─── Banner component ─────────────────────────────────────────────────────────

export default function CookieConsent() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      if (getCookieConsent() === null) setShow(true);
    } catch { /* SSR / blocked storage */ }
  }, []);

  const accept = () => {
    saveConsent(true);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        // sit ABOVE the bottom nav (~64px) + iOS safe area, so it never covers it
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)',
        zIndex: 4000,
        background: '#111827',
        color: '#f9fafb',
        borderRadius: 14,
        boxShadow: '0 8px 30px rgba(0,0,0,0.28)',
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        maxWidth: 640,
        margin: '0 auto',
        animation: 'hlCookieIn 0.35s cubic-bezier(0.16,1,0.3,1) both',
        flexWrap: 'wrap',
      }}
    >
      <style>{`@keyframes hlCookieIn{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
      {/* Lee mascot (BakeLee) — friendlier than the bare emoji */}
      <img
        src={bakeLee}
        alt=""
        aria-hidden
        style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
      />
      <span style={{ flex: 1, minWidth: 160, fontSize: 12.5, lineHeight: 1.45, color: '#e5e7eb' }}>
        {t('cookies.message', 'We use cookies to give you the best experience. By continuing to use HonestLee you accept our use of cookies.')}
      </span>
      {/* Single acknowledgement — no opt-out (Patrick 2026-06-14: a simple cookie
          notice is enough; people shouldn't be able to opt out). */}
      <button
        onClick={accept}
        style={{
          flexShrink: 0,
          background: '#E46E34',
          color: '#fff',
          border: 'none',
          borderRadius: 10,
          padding: '9px 16px',
          fontWeight: 800,
          fontSize: 13,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {t('cookies.accept', 'Got it')}
      </button>
    </div>
  );
}
